import * as React from 'react'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import GetAppIcon from '@mui/icons-material/GetApp'
import IosShareIcon from '@mui/icons-material/IosShare'
import useInstallPrompt from '../../hooks/useInstallPrompt'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'

/**
 * Shows a "Add to Home Screen" prompt banner for iOS and Android PWA installs.
 * - Android: triggers the native browser install prompt
 * - iOS: shows manual share-sheet instructions (Safari only)
 * Dismissed state persists 30 days in localStorage.
 */
const InstallPromptBanner: React.FC = () => {
  const { canInstall, isIos, promptInstall, dismiss } = useInstallPrompt()
  const { pathname, search } = useLocation()
  const { t } = useTranslation()

  // Only rendered for logged in users (see App.tsx). Additionally never show it on
  // shared links (access token), embedded views or the thank-you page after a shared
  // process, even if the person opening the link happens to be logged in.
  const isSharedLink = new URLSearchParams(search).has('access_token')
    || pathname.startsWith('/embedded')
    || pathname.startsWith('/resultset/')
    || pathname === '/thanks'
    || pathname.startsWith('/link/')

  if (!canInstall || isSharedLink) return null

  return (
    <Paper
      elevation={4}
      sx={{
        position: 'fixed',
        bottom: { xs: 64, sm: 16 }, // above bottom nav on mobile
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1400,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        maxWidth: 420,
        width: 'calc(100% - 32px)',
        bgcolor: 'background.paper',
      }}
    >
      {isIos
        ? <IosShareIcon color="primary" sx={{ flexShrink: 0 }} />
        : <GetAppIcon color="primary" sx={{ flexShrink: 0 }} />
      }

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap>
          {t('installPrompt.title')}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {isIos
            ? t('installPrompt.iosHint')
            : t('installPrompt.androidHint')
          }
        </Typography>
      </Box>

      {!isIos && (
        <Button
          size="small"
          variant="contained"
          onClick={promptInstall}
          sx={{ flexShrink: 0 }}
        >
          {t('installPrompt.install')}
        </Button>
      )}

      <IconButton size="small" onClick={dismiss} aria-label={t('installPrompt.dismiss')}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  )
}

export default InstallPromptBanner
